import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useSettings } from "@/context/SettingsContext";
import { fileUrl } from "@/lib/api";

function upsert(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(attrs.tag || "meta");
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => { if (k !== "tag" && v != null) el.setAttribute(k, v); });
  return el;
}

export function Seo({ title, description, image, type = "website", noindex = false, jsonLd, keywords }) {
  const { settings } = useSettings();
  const { pathname } = useLocation();

  useEffect(() => {
    const brand = settings.brand_name || "Safarshala";
    const siteUrl = (settings.site_url || window.location.origin).replace(/\/$/, "");
    const url = `${siteUrl}${pathname}`;
    const fullTitle = title ? `${title} | ${brand}` : (settings.seo_title || `${brand} — ${settings.tagline || "Curated Journeys Across India"}`);
    const desc = description || settings.seo_description || settings.tagline || "";
    const img = fileUrl(image || settings.og_image_url) || `${siteUrl}/logo.png`;
    const absImg = img.startsWith("http") ? img : `${siteUrl}${img}`;
    const robots = noindex || settings.seo_index === false ? "noindex, nofollow" : "index, follow";

    document.title = fullTitle;
    upsert('meta[name="description"]', { name: "description", content: desc });
    upsert('meta[name="keywords"]', { name: "keywords", content: keywords || settings.seo_keywords || "" });
    upsert('meta[name="robots"]', { name: "robots", content: robots });
    upsert('link[rel="canonical"]', { tag: "link", rel: "canonical", href: url });
    upsert('meta[property="og:title"]', { property: "og:title", content: fullTitle });
    upsert('meta[property="og:description"]', { property: "og:description", content: desc });
    upsert('meta[property="og:type"]', { property: "og:type", content: type });
    upsert('meta[property="og:url"]', { property: "og:url", content: url });
    upsert('meta[property="og:image"]', { property: "og:image", content: absImg });
    upsert('meta[property="og:site_name"]', { property: "og:site_name", content: brand });
    upsert('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    upsert('meta[name="twitter:title"]', { name: "twitter:title", content: fullTitle });
    upsert('meta[name="twitter:description"]', { name: "twitter:description", content: desc });
    upsert('meta[name="twitter:image"]', { name: "twitter:image", content: absImg });
    if (settings.google_site_verification) upsert('meta[name="google-site-verification"]', { name: "google-site-verification", content: settings.google_site_verification });

    const ld = document.getElementById("seo-jsonld") || Object.assign(document.createElement("script"), { id: "seo-jsonld", type: "application/ld+json" });
    if (!ld.parentNode) document.head.appendChild(ld);
    const org = {
      "@context": "https://schema.org", "@type": "TravelAgency", name: brand, url: siteUrl, logo: `${siteUrl}/logo.png`,
      description: settings.seo_description || settings.tagline || undefined,
      telephone: settings.contact_number || undefined, email: settings.email || undefined,
      sameAs: [settings.instagram, settings.facebook, settings.twitter].filter(Boolean),
    };
    ld.textContent = JSON.stringify(jsonLd ? [org, jsonLd] : org);
  }, [title, description, image, type, noindex, keywords, jsonLd, settings, pathname]);

  return null;
}
